import Vue from "vue";
import {playerService} from "src/modules/player/models/PlayerService";
import {schoolService} from "src/modules/school/SchoolService";
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { analyticsController } from '../user/analytics/AnalyticsController';
import { BackEndService } from 'src/utils/BackEndService';
import { playerConfig } from "../player/models/PlayerConfig";
import {TinyEmitter} from 'tiny-emitter';
import {
  PageContext,
  TeacherChatMessageConfig,
  TeacherSuggestion,
  TeacherAction,
  ActionPayload
} from './TeacherTypes';
import {homeController} from "src/modules/catalog/services/HomeController";
import {userService} from "src/modules/user/UserService";

class TeacherService extends TinyEmitter {
  private ctrl: AbortController = undefined;
  private hasReceivedEveryToken: boolean = false;
  private timeWaitingMissingTokens: number = 0;
  private currentMessage: object[] = [];

  public readonly state = Vue.observable({
    currentPageContent: null as PageContext | null,
    messages: [] as TeacherChatMessageConfig[],
    conversationId: null as number | null,
    canDisplayResponse: false as boolean,
    canceledMessage: '' as string,
    timeWithNoResponse: 0 as number,
    teacherMessage: null as TeacherSuggestion | null,
  });

  public changePageContext(newPageContext: PageContext) {
    this.state.currentPageContent = newPageContext;
  }

  public getCurrentPlayableContext() {
    if (schoolService.state.activeModule) {
      const activeStep = schoolService.state.activeStep as any;
      return {
        playableType: 'school',
        moduleTitle: schoolService.state.activeModule.title,
        moduleDescription: schoolService.state.activeModule.description,
        exerciseTitle: activeStep?.name || '',
        exerciseDescription: activeStep?.description || '',
      }
    }
    else {
      const content = playerService.state.playerPlayableContent as any;
      return {
        playableType: playerService.state.playableContentType,
        name: content.name,
        difficulty: content?.difficulty || '',
        pieceName: content?.piece?.name || '',
        pieceArtist: content?.piece?.artist || '',
      }
    }
  }

  public addMessage(message: TeacherChatMessageConfig): void {
    this.state.messages.push(message);
  }

  public sendText(text: string, isListening: boolean = false, isConvDisplayed: boolean = false, route: any = null, isInPlayer: boolean = false, context: object = null): void {
    if (text !== "") {
      this.addMessage({message: text, fromUser: true, loading: false, generating: false, isHtml: false});
      analyticsController.logBasicAnalyticsEvent('user sent message to teacher', 'Teacher Chatbot', {message: text});
      
      // Décrémenter le quota de messages du professeur pour les utilisateurs non premium
      if (!userService.user.premium) {
        userService.updateConfiguration('CURRENT_WEEKLY_FREE_TEACHER_MESSAGES', 
          userService.user.configurations.currentWeeklyFreeTeacherMessages - 1);
        
        analyticsController.logBasicAnalyticsEvent('user decremented weekly free teacher messages', 'subscribtion', {
          page: route?.meta?.title || '',
          currentWeeklyFreeTeacherMessages: userService.user.configurations.currentWeeklyFreeTeacherMessages,
        })
      }
      
      this.sendRequest(text, false, isListening, isConvDisplayed, route, isInPlayer, context);
    }
  }

  public sendRequest(text: string, fromTeacher: boolean = false, isListening: boolean = false, isConvDisplayed: boolean = false, route: any = null, isInPlayer: boolean = false, context: object = null): void {
    this.ctrl = new AbortController();
    const parameters: any = {
      text: text,
      id: this.state.conversationId,
      page: route?.meta?.title || '',
      canceledMessage: this.state.canceledMessage,
      fromVoice: isListening,
      pageContext: JSON.stringify(this.state.currentPageContent),
      date: new Date().toLocaleString(),
      pieceCourses: JSON.stringify({
        total_progress: homeController.pieceCoursesTotalProgress,
        courses: homeController.state.pieceCourses.map(course => ({
          title: course.piece.title,
          difficulty: course.difficulty,
          progress: homeController.pieceProgressPercent(course)
        }))
      }),
      levelProgression: (homeController.levelsWithProgress.find(
        l => l.value === userService.user.level.value
      ).progress * 100).toFixed(2) + '%'

    };

    if (isInPlayer) {
      parameters.playableContentInfos = JSON.stringify(this.getCurrentPlayableContext());
    }

    if(fromTeacher) {
      parameters.fromTeacher = true;
    }

    if (context) {
      parameters.explainKeyword = true;
      parameters.context = JSON.stringify(context);
    }

    const param = BackEndService.parametersToGetRequest(parameters);
    this.currentMessage = [];
    this.state.canceledMessage = null;
    this.hasReceivedEveryToken = false;

    if (!isConvDisplayed) {
      this.newSuggestion({
        message: "",
        loading: true,
        timeout: 10000
      });
    }

    fetchEventSource(process.env.API_URL! + process.env.API_VERSION! + 'chatbot/send-message' + param, {
      method: 'GET',
      headers: {'Authorization': 'Bearer ' + localStorage.tokenTokatab},
      openWhenHidden: true,
      onmessage: (msg) => this.addAnswer(msg, isConvDisplayed, route, isInPlayer),
      onerror: () => this.ctrl.abort(),
      onclose: () => this.ctrl.abort(),
      signal: this.ctrl.signal,
    });

    this.addMessage({message: '', fromUser: false, loading: true, generating: true, isHtml: false});
    this.state.canDisplayResponse = true;

    setTimeout(() => {
      setTimeout(() => {
        if(this.state.messages[this.state.messages.length - 1].loading) {
          this.state.timeWithNoResponse = 10;
        }
      }, 10000);
      setTimeout(() => {
        if(this.state.messages[this.state.messages.length - 1].loading) {
          this.state.timeWithNoResponse = 30;
        }
      }, 30000);
    }, 1000);
  }

  public addAnswer(event: any, isConvDisplayed: boolean = false, route: any = null, isInPlayer: boolean = false): void {
    if(this.state.canDisplayResponse) {
      const lastMessageIndex = this.state.messages.length - 1;
      if(['update', 'redirect', 'recommendation', 'skillRecommendation', 'skillCategories'].includes(event.event)) {
        this.state.timeWithNoResponse = 0;
      }
      if (event.data === "<END_STREAMING_SSE>") {
        this.endStreaming();
        return;
      }
      if (event.event === 'update') {
        this.currentMessage.push(JSON.parse(event.data));
        this.state.messages[lastMessageIndex].message = this.getCurrentGeneratingMessage();
        if (this.state.messages[lastMessageIndex].message.length >= 1) {
          this.state.messages[lastMessageIndex].loading = false;
        }
      }
      else if (event.event === 'sendConvId') {
        this.state.conversationId = event.data;
      }
      else if (event.event === 'redirect') {
        this.state.messages[lastMessageIndex].tool = 'redirect';
        this.state.messages[lastMessageIndex].tool_details = event.data;
      }
      else if (event.event === 'recommendation') {
        this.state.messages[lastMessageIndex].tool = 'recommendation';
        this.state.messages[lastMessageIndex].tool_details = JSON.parse(event.data).slice(0, 4);
      }
      else if (event.event === 'skillRecommendation') {
        this.state.messages[lastMessageIndex].tool = 'skillRecommendation';
        this.state.messages[lastMessageIndex].tool_details = JSON.parse(event.data);
      }
      else if (event.event === 'skillCategories') {
        this.state.messages[lastMessageIndex].tool = 'skillCategories';
        this.state.messages[lastMessageIndex].tool_details = JSON.parse(event.data);
      }
      else if (event.event === 'showPlayerParameter') {
        this.state.messages[lastMessageIndex].tool = 'showPlayerParameter';
        this.state.messages[lastMessageIndex].tool_details = event.data;

        const payload: ActionPayload = {
          action: TeacherAction.SHOW_PLAYER_PARAMETER,
          parameter: event.data
        };
        this.emit(TeacherAction.SHOW_PLAYER_PARAMETER, payload);
      }
      else if (event.event === 'doAction') {
        const data = JSON.parse(event.data);
        let actionMessage = "";
        let actionType: TeacherAction | null = null;

        if (data.action === 'redirect') {
          actionType = TeacherAction.REDIRECT;
          actionMessage = "Je vous redirige vers la page demandée.";
        }
        else if (isInPlayer) {
          if (data.action === 'pause') {
            actionType = TeacherAction.PAUSE;
            actionMessage = "J'ai mis le morceau en pause.";
          }
          else if (data.action === 'restart') {
            actionType = TeacherAction.RESTART;
            actionMessage = "J'ai redémarré le morceau.";
          }
          else if (data.action === 'play') {
            actionType = TeacherAction.PLAY;
            actionMessage = "J'ai démarré le morceau.";
          }
          else if (data.action === 'validate') {
            actionType = TeacherAction.VALIDATE;
            actionMessage = "Bravo, j'ai validé le contenu en cours !";
          }
          else if (data.action === 'change_tempo') {
            actionType = TeacherAction.CHANGE_TEMPO;
            data.parameter = data.parameter[0].toUpperCase() + data.parameter.slice(1);

            if (data.parameter === 'Decrease') {
              actionMessage = "J'ai ralenti le tempo du morceau.";
            }
            else if (data.parameter === 'Increase') {
              actionMessage = "J'ai accéléré le tempo du morceau.";
            }
            else {
              actionMessage = "J'ai modifié le tempo du morceau.";
            }
          }
          else if (data.action === 'increase_volume') {
            actionType = TeacherAction.INCREASE_VOLUME;
            actionMessage = `J'ai augmenté le volume demandé.`;
          }
          else if (data.action === 'decrease_volume') {
            actionType = TeacherAction.DECREASE_VOLUME;
            actionMessage = `J'ai diminué le volume demandé.`;
          }
          else if (data.action === 'toggle_volume') {
            actionType = TeacherAction.TOGGLE_VOLUME;
            actionMessage = !playerConfig.state.soundParameters.actives[data.parameter] ?
              `J'ai activé le volume demandé.` :
              `J'ai désactivé le volume demandé.`;
          }
        }

        if (actionType) {
          const payload: ActionPayload = {
            action: actionType,
            parameter: data.parameter,
            message: actionMessage
          };
          this.emit(actionType, payload);
        }

        this.state.messages[lastMessageIndex].message = actionMessage;
        this.endStreaming(true);
      }
      else if (event.event === 'contactTeam') {
        this.state.messages[lastMessageIndex].tool = 'contactTeam';
      }
      else if(event.event === 'full') {
        console.log(JSON.parse(event.data));
      }
    }
    else {
      setTimeout(() => this.addAnswer(event, isConvDisplayed, route, isInPlayer), 500);
    }
  }

  public endStreaming(forceStop: boolean = false): void {
    if(this.hasReceivedEveryToken || this.timeWaitingMissingTokens > 5000 || forceStop) {
      this.ctrl.abort();
      analyticsController.logBasicAnalyticsEvent('teacher replied to user', 'Teacher Chatbot');
      this.state.canDisplayResponse = false;
      this.state.messages[this.state.messages.length - 1].generating = false;
      this.state.messages[this.state.messages.length - 1].loading = false;

      // Suggestion
      if (!this.state.messages[this.state.messages.length - 1].fromUser) {
        const responseMessage = this.state.messages[this.state.messages.length - 1].message;
        if (responseMessage) {
          this.newSuggestion({
            message: responseMessage,
            loading: false,
            timeout: 5000
          });
        }
      }

      this.currentMessage = [];
      this.timeWaitingMissingTokens = 0;
      this.state.canDisplayResponse = true;
    }
    else {
      this.timeWaitingMissingTokens += 1000;
      setTimeout(() => this.endStreaming(), 1000);
    }
  }

  public cancelMessage(): void {
    this.ctrl.abort();
    this.state.messages[this.state.messages.length - 1].generating = false;
    this.state.messages[this.state.messages.length - 1].loading = false;
    this.state.timeWithNoResponse = -1;
    this.state.canDisplayResponse = false;
    this.state.canceledMessage = this.state.messages[this.state.messages.length - 1].message;
    analyticsController.logBasicAnalyticsEvent('user canceled chatbot response', 'Teacher Chatbot');
  }

  private getCurrentGeneratingMessage(): string {
    const tokens = [...this.currentMessage];
    tokens.sort((a: any, b: any) => (a as any).id - (b as any).id);
    this.hasReceivedEveryToken = tokens.length > 0 &&
      tokens.length - 1 === (tokens[tokens.length - 1] as any).id - 1;
    return tokens.reduce((acc: string, text: any) => acc + (text as any).text, "").replace(/__NEWLINE__/g, '\n');
  }

  public showPlayerParameterWithTour(parameter: string): void {
    if ((parameter === 'displayFingerOrNotes' || parameter === 'sheetMusicMode')) {
      parameter = "MobileDisplayMenu";
    }
    const payload: ActionPayload = {
      action: TeacherAction.SHOW_PLAYER_PARAMETER,
      parameter: parameter
    };
    this.emit(TeacherAction.SHOW_PLAYER_PARAMETER, payload);
  }

  public newSuggestion(suggestion: TeacherSuggestion): void {
    if (suggestion === this.state.teacherMessage) {
      return;
    }
    this.state.teacherMessage = suggestion;
  }

  public dismissSuggestion(suggestion: TeacherSuggestion = null): void {
    if (this.state.teacherMessage === suggestion || suggestion === null) {
      this.state.teacherMessage = null;
    }
  }

  get isGenerating() {
    return this.state.messages[this.state.messages.length - 1].generating
  }
}

export const teacherService = new TeacherService();


